import { Voucher } from '../types';
import { documentTypesEnum, documentTypesInitialsEnum } from '../helpers';
import { PdfTablePropsColumn, PdfTablePropsRow } from '../../../components/Pdfs/PdfComponents/PdfTable';
import { BillingContactPDFType } from '../../../components/Pdfs/PdfComponents/PdfBillingContactDetails';
import { taytanFormatNumberToString } from '../../../utils/mathUtils';
import { getPdfSupplierInfo } from '../../supplierAccounts/helpers';
import { getPdfAgentInfo } from '../../travelAgencyAccounts/helpers';
import { PdfInfoBuilderContext, BuiltPdfInfo } from './types';
import {
    getCurrencyDetails,
    getClientCurrencies,
    formatVoucherDate,
    buildRateLine,
    coveredDocumentRef,
    convertCoveredAmountToCovering,
    documentPrimaryToLocalSource
} from './helpers';
import { resolveDocumentLocalRate, resolveDocumentPrimaryToLocalRate, sameCurrency } from '../../../utils/currencyMath';

/**
 * Get account label for Credit/Debit note table
 * Returns label like "Travel File#123", "Supplier Name", "Agent Name", etc.
 */
function getAccountLabel(
    type: string | undefined,
    number: string | number | undefined,
    name: string | undefined
): string {
    if (!type) return '---';

    switch (type) {
        case 'client':
            return `Travel File#${number || ''}`;
        case 'group':
            return `Group Travel File#${number || ''}`;
        case 'supplier':
            return name || `Supplier#${number || ''}`;
        case 'agent':
            return name || `Agent#${number || ''}`;
        case 'account':
            return name || `Account#${number || ''}`;
        default:
            return `${type}#${number || ''}`;
    }
}

/**
 * Build pdfInfo for Credit Note / Debit Note
 */
export function buildCreditDebitPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const pdfInfo = info?.pdfInfo;
    const storedPdfRows = info?.pdfRows;
    const { code: currencyCode } = getCurrencyDetails(info?.currency, context.currencies);
    const amount = Number(info?.amount) || 0;

    // Get passenger info
    const rawPassenger = voucher.passenger;
    const fromVoucherCd = Array.isArray(rawPassenger) ? rawPassenger[0] : rawPassenger;
    const passenger = (fromVoucherCd?.name || fromVoucherCd?.firstName) ? fromVoucherCd : (pdfInfo?.passenger || info?.passenger || info?.beneficiary?.[0] || null);

    // Determine if this is a Credit Note or Debit Note
    const isCredit = voucher.type === documentTypesEnum.CREDIT_DEBIT;
    const creditOrDebit = isCredit ? 'Credit' : 'Debit';

    // Use stored pdfInfo if available (from frontend), otherwise build dynamically
    let columns: PdfTablePropsColumn[];
    let rows: PdfTablePropsRow[];
    let summaryLine: PdfTablePropsRow;

    if (pdfInfo?.columns && pdfInfo?.rows) {
        // Use pre-built data from frontend
        columns = pdfInfo.columns;
        rows = pdfInfo.rows;
        summaryLine = pdfInfo.summaryLine || { debit: 'Total', amount: taytanFormatNumberToString(amount), currency: currencyCode };
    } else if (storedPdfRows?.columns?.length && storedPdfRows?.rows?.length) {
        // Use persisted rows from documentinfo when available.
        columns = storedPdfRows.columns;
        rows = storedPdfRows.rows;
        summaryLine = storedPdfRows.summaryLine || { debit: 'Total', amount: taytanFormatNumberToString(amount), currency: currencyCode };
    } else {
        // Build dynamically from voucher data
        // Get labels for source and target accounts.
        // Source and target labels must be resolved independently.
        const sourceName =
            voucher.sourceType === 'supplier'
                ? info?.supplierName
                : voucher.sourceType === 'agent'
                    ? info?.travelAgentName
                    : voucher.sourceType === 'account'
                        ? info?.accountName
                        : undefined;
        const targetName =
            voucher.targetType === 'supplier'
                ? info?.supplierName
                : voucher.targetType === 'agent'
                    ? info?.travelAgentName
                    : voucher.targetType === 'account'
                        ? info?.accountName
                        : undefined;
        const sourceLabel = getAccountLabel(
            voucher.sourceType,
            voucher.sourceNumber,
            sourceName
        );
        const targetLabel = getAccountLabel(
            voucher.targetType,
            voucher.targetNumber,
            targetName
        );

        // Credit Note: source is debited, target is credited
        // Debit Note: source is debited, target is credited (same mapping, different column order)
        if (isCredit) {
            columns = [
                { title: 'Credit', fieldName: 'credit' },
                { title: 'Debit', fieldName: 'debit' },
                { title: 'Amount', fieldName: 'amount' },
                { title: 'Currency', fieldName: 'currency' }
            ];
            rows = [{
                credit: targetLabel,
                debit: sourceLabel,
                amount: taytanFormatNumberToString(amount),
                currency: currencyCode
            }];
            summaryLine = {
                credit: 'Total',
                amount: taytanFormatNumberToString(amount),
                currency: currencyCode
            };
        } else {
            columns = [
                { title: 'Debit', fieldName: 'debit' },
                { title: 'Credit', fieldName: 'credit' },
                { title: 'Amount', fieldName: 'amount' },
                { title: 'Currency', fieldName: 'currency' }
            ];
            rows = [{
                debit: sourceLabel,
                credit: targetLabel,
                amount: taytanFormatNumberToString(amount),
                currency: currencyCode
            }];
            summaryLine = {
                debit: 'Total',
                amount: taytanFormatNumberToString(amount),
                currency: currencyCode
            };
        }
    }

    // Build contact info
    let agentInfo: BillingContactPDFType | null = pdfInfo?.agentInfo || null;
    let supplierInfo: BillingContactPDFType | null = pdfInfo?.supplierInfo || null;

    if (voucher.sourceType === 'agent' && context.travelAgencyAccount) {
        agentInfo = getPdfAgentInfo(context.travelAgencyAccount);
    }
    if (voucher.sourceType === 'supplier' && context.supplierAccount) {
        supplierInfo = getPdfSupplierInfo(context.supplierAccount);
    }

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        agentInfo,
        supplierInfo,
        passenger,
        topLeftDetailsText: pdfInfo?.topLeftDetailsText || null,
        creditOrDebit
    };
}

/**
 * Build pdfInfo for PRQ (Payment Requisition)
 * Shows breakdown: Amount, Commission, VAT on commission, Tax, Net to Pay
 *
 * Fixes from audit:
 * - Field name: `supplierDetails` (not `supplierInfo`) — PRQPdf uses PdfSupplierDetails component
 * - 13-column vouchers table matching inline (was 4 columns)
 * - Added passenger field
 */
export function buildPRQPdfInfo(
    voucher: Voucher,
    context: PdfInfoBuilderContext,
    baseInfo: BuiltPdfInfo
): BuiltPdfInfo {
    const info = voucher.info as any;
    const { code: currencyCode, symbol: currencySymbol } = getCurrencyDetails(info?.currency, context.currencies);
    const { localCurrency } = getClientCurrencies(context.client, context.currencies);
    const localLabel = localCurrency?.code || '';
    const rate = context.getConvertRate(info?.currency, localCurrency);
    const rateToLocal = Number(info?.rate) || rate?.exchangeRateSell || 1;
    const showLocal = localLabel && currencyCode !== localLabel;

    // ── Main table: single currency column (per TB spec — no local currency column on PRQ)
    const columns: PdfTablePropsColumn[] = [
        { title: 'Details', fieldName: 'title', style: { width: '70%' } },
        { title: currencyCode, fieldName: 'content', style: { width: '30%' } }
    ];

    const supplierPrice = Number(info?.supplierPrice) || Number(info?.amount) || 0;
    const commission = Number(info?.commission) || 0;
    const vatOnCommission = Number(info?.vatOnCommission) || 0;
    const tax = Number(info?.tax) || 0;
    const netToPay = supplierPrice - commission + tax;

    const makeRow = (title: string, value: number): PdfTablePropsRow => {
        return { title, content: taytanFormatNumberToString(value) };
    };

    const rows: PdfTablePropsRow[] = [
        makeRow('Amount', supplierPrice),
        makeRow('Commission', commission),
        makeRow('VAT on Commission', vatOnCommission),
        makeRow('Tax', tax)
    ];

    const summaryLine: PdfTablePropsRow = { title: `Net to Pay`, content: `${taytanFormatNumberToString(netToPay)}  ${currencyCode}` };

    // ── Rate line ────────────────────────────────────────────────────────────
    const rateLine = showLocal ? buildRateLine(currencyCode, rateToLocal, localLabel) : '';

    // ── Supplier details (PRQPdf uses PdfSupplierDetails, not PdfBillingContactDetails) ──
    let supplierDetails: any = null;
    if (context.supplierAccount) {
        supplierDetails = {
            address: context.supplierAccount.address || {},
            phones: context.supplierAccount.phones || []
        };
    } else if (info?.pdfInfo?.supplierDetails) {
        supplierDetails = info.pdfInfo.supplierDetails;
    }

    // ── Passenger ────────────────────────────────────────────────────────────
    const rawPassenger = voucher.passenger;
    const fromVoucher = Array.isArray(rawPassenger) ? rawPassenger[0] : rawPassenger;
    const passengerCandidate = (fromVoucher?.name || fromVoucher?.firstName) ? fromVoucher : (info?.pdfInfo?.passenger || info?.passenger || info?.beneficiary?.[0] || null);
    // Only use passenger if it has a name — prevents empty object from hiding topLeftDetailsText
    const passenger = (passengerCandidate?.name || passengerCandidate?.firstName) ? passengerCandidate : null;

    // ── Vouchers table (page 2): 13-column matching inline ───────────────────
    let columnsVouchers: PdfTablePropsColumn[] | null = null;
    let rowsVouchers: PdfTablePropsRow[] | null = null;
    let summaryLineVouchers: PdfTablePropsRow | null = null;

    const coveredDocuments = context.coveredDocuments;
    if (coveredDocuments && coveredDocuments.length > 0) {
        columnsVouchers = [
            { title: 'Type', fieldName: 'docType', style: { width: '8%' } },
            { title: 'Doc#', fieldName: 'number', style: { width: '9%' } },
            { title: 'Value Date', fieldName: 'dueDate', style: { width: '10%' } },
            { title: 'T. File #', fieldName: 'travelFileNumber', style: { width: '9%' } },
            // { title: 'Tour Codes', fieldName: 'tourCode', style: { width: '8%' } },
            // { title: 'Lead Passenger', fieldName: 'passengerName', style: { width: '10%' } },
            { title: 'Service', fieldName: 'serviceName', style: { width: '11%' } },
            { title: 'Ref.', fieldName: 'ref', style: { width: '9%' } },
            { title: 'Amount', fieldName: 'amount', style: { width: '10%' } },
            { title: 'Comm.', fieldName: 'commission', style: { width: '7%' } },
            { title: 'VAT', fieldName: 'vat', style: { width: '6%' } },
            { title: 'Tax', fieldName: 'tax', style: { width: '6%' } },
            { title: 'Net Remit', fieldName: 'netToRemit', style: { width: '10%' } },
            { title: 'Curr.', fieldName: 'currency', style: { width: '5%' } }
        ];

        let totalAmount = 0;
        let totalCommission = 0;
        let totalVat = 0;
        let totalTax = 0;
        let totalNetToRemit = 0;

        // The TOTAL line is in the PRQ's currency, and a PRQ need not be in its
        // vouchers' currency — so each voucher is converted at the PRQ's own
        // rates, as the remittance PDF does. Adding them raw printed a $100
        // voucher as "100.00₪" under a shekel PRQ.
        const { localCurrency: prqLocal, primaryCurrency: prqPrimary } = getClientCurrencies(
            context.client,
            context.currencies
        );
        const prqCurrencyId = info?.currency && typeof info.currency === 'object' ? info.currency._id : info?.currency;
        const marketOf = (from: unknown, to: unknown) =>
            Number(context.getConvertRate?.(from, to)?.exchangeRateSell) || 0;
        const prqLocalRate =
            resolveDocumentLocalRate({
                currencyId: prqCurrencyId,
                localId: prqLocal?._id,
                storedLocalRate: info?.localRate,
                amount: info?.amount,
                amountLocal: info?.amountLocal,
                fallbackRate: marketOf(prqCurrencyId, prqLocal?._id)
            }) || 0;
        const prqPrimaryToLocal = resolveDocumentPrimaryToLocalRate({
            currencyId: prqCurrencyId,
            primaryId: prqPrimary?._id,
            localId: prqLocal?._id,
            storedRate: documentPrimaryToLocalSource(info, prqLocal?._id, prqPrimary?._id),
            storedPrimaryRate: info?.primaryRate,
            marketPrimaryToLocal: sameCurrency(prqPrimary?._id, prqLocal?._id)
                ? 1
                : marketOf(prqPrimary?._id, prqLocal?._id)
        });

        rowsVouchers = coveredDocuments.map((doc) => {
            const docInfo = (Array.isArray(doc.info) ? doc.info[0] : doc.info) as any;
            const amt = Number(docInfo?.amount) || 0;
            const comm = Number(docInfo?.markup) || Number(docInfo?.commission) || 0;
            const vat = Number(docInfo?.vatOnCommission) || 0;
            const docTax = Number(docInfo?.tax) || 0;
            const netRemit = Number(docInfo?.netToRemit) || amt;

            const toPrq = (n: number) =>
                convertCoveredAmountToCovering({
                    amount: n,
                    docInfo,
                    coveringCurrencyId: prqCurrencyId,
                    localId: prqLocal?._id,
                    primaryId: prqPrimary?._id,
                    coveringLocalRate: prqLocalRate,
                    coveringPrimaryToLocal: prqPrimaryToLocal,
                    getConvertRate: context.getConvertRate
                });
            totalAmount += toPrq(amt);
            totalCommission += toPrq(comm);
            totalVat += toPrq(vat);
            totalTax += toPrq(docTax);
            totalNetToRemit += toPrq(netRemit);

            const typeInitial = (documentTypesInitialsEnum as any)[doc.type] || '';
            const docPassenger = Array.isArray((doc as any).passenger)
                ? (doc as any).passenger[0]
                : (doc as any).passenger;
            const passengerName = docPassenger
                ? `${docPassenger.firstName || ''} ${docPassenger.lastName || ''}`.trim()
                : docInfo?.passengerName || '';

            return {
                docType: typeInitial,
                number: doc.number != null ? String(doc.number) : '',
                dueDate: formatVoucherDate(docInfo?.dueDate),
                travelFileNumber: docInfo?.travelFileNumber || '',
                tourCode: docInfo?.tourCode || docInfo?.code1 || '',
                passengerName,
                serviceName: docInfo?.serviceName || '',
                ref: coveredDocumentRef(docInfo),
                amount: taytanFormatNumberToString(amt),
                commission: taytanFormatNumberToString(comm),
                vat: taytanFormatNumberToString(vat),
                tax: taytanFormatNumberToString(docTax),
                netToRemit: taytanFormatNumberToString(netRemit),
                currency: docInfo?.currency?.code || getCurrencyDetails(docInfo?.currency, context.currencies).code || ''
            };
        });

        summaryLineVouchers = {
            docType: 'TOTAL',
            number: `(${currencyCode})`,
            dueDate: '', travelFileNumber: '', tourCode: '', passengerName: '', serviceName: '', ref: '',
            amount: taytanFormatNumberToString(totalAmount) + (currencySymbol || ''),
            commission: taytanFormatNumberToString(totalCommission) + (currencySymbol || ''),
            vat: taytanFormatNumberToString(totalVat) + (currencySymbol || ''),
            tax: taytanFormatNumberToString(totalTax) + (currencySymbol || ''),
            netToRemit: taytanFormatNumberToString(totalNetToRemit) + (currencySymbol || ''),
            currency: currencyCode
        };
    }

    // Group covered docs by type: "VCH: 1000158, 1000159 | PRQ: 4000088"
    let linkedTo = '';
    if (coveredDocuments?.length) {
        const grouped: Record<string, string[]> = {};
        for (const doc of coveredDocuments) {
            const typeKey = (documentTypesInitialsEnum as any)[doc.type] || doc.type;
            if (!grouped[typeKey]) grouped[typeKey] = [];
            grouped[typeKey].push(String(doc.number || ''));
        }
        linkedTo = Object.entries(grouped).map(([type, nums]) => `${type}: ${nums.join(', ')}`).join('  |  ');
    }

    return {
        ...baseInfo,
        columns,
        rows,
        summaryLine,
        rateLine,
        supplierDetails,
        passenger,
        topLeftDetailsText: info?.supplierName || null,
        bottomDetails: info?.for || '',
        // How the supplier is to be paid. Rendered as its own titled section —
        // the main table is a Details/amount key-value grid, so a text value
        // has no column to live in.
        formOfPayment: info?.paymentMethodType || '',
        linkedTo,
        columnsVouchers,
        rowsVouchers,
        summaryLineVouchers
    };
}
